import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Layout components
import Layout from "./components/Layout/Layout";

// Main Pages
// Removed top-level Dashboard import as we're reorganizing the structure
import TechnicalAnalysis from "./pages/TechnicalAnalysis";
import FundamentalAnalysis from "./pages/FundamentalAnalysis";
import RiskManagement from "./pages/RiskManagement";
import TradeHistory from "./pages/TradeHistory";
import Execution from "./pages/Execution";
import StrategyBuilder from "./pages/StrategyBuilder";
import Documentation from "./pages/Documentation";
import LandingPage from "./pages/LandingPage";
import TradingDashboard from "./pages/TradingDashboard";
import StrategyDashboard from "./pages/strategy/StrategyDashboard";

// Auth Pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";

// User Pages
import UserDashboard from "./pages/user/Dashboard";
import UserPlans from "./pages/user/Plans";
import UserTickets from "./pages/user/Tickets";
import UserSettings from "./pages/user/Settings";
import ApiConnections from "./pages/user/ApiConnections";
import TradingViewWebhook from "./pages/user/TradingViewWebhook";
import Monitor from "./pages/Monitor";
import MonitorErrorBoundary from "./components/MonitorErrorBoundary";
import { installCapture } from "./services/MonitorCapture";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminPlans from "./pages/admin/Plans";
import AdminTickets from "./pages/admin/Tickets";
import AdminUsers from "./pages/admin/Users";
import AdminBlog from "./pages/admin/Blog";
import AdminPayments from "./pages/admin/Payments";
import AdminNewsletter from "./pages/admin/Newsletter";
import AdminMarketing from "./pages/admin/Marketing";
import AdminSettings from "./pages/admin/Settings";

// Install monitor capture (console/error interception) — dev tool
if (process.env.REACT_APP_ENABLE_MONITOR_PAGE !== 'false') {
  installCapture();
}

// Create theme — matches Trade History glassmorphic dark design
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00FF94",
      light: "#33FFB0",
      dark: "#00CC76",
    },
    secondary: {
      main: "#60A5FA",
    },
    success: {
      main: "#10B981",
    },
    error: {
      main: "#EF4444",
    },
    warning: {
      main: "#F59E0B",
    },
    background: {
      default: "#0B1120",
      paper: "#141821",
    },
    text: {
      primary: "#F3F4F6",
      secondary: "#9CA3AF",
    },
    divider: "rgba(255,255,255,0.08)",
  },
  typography: {
    fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
          backgroundColor: 'rgba(20,24,33,0.8)',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(20,24,33,0.9)',
          border: '1px solid rgba(255,255,255,0.06)',
          '@media (max-width:600px)': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#0B1120',
            color: '#9CA3AF',
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          },
          '& .MuiTableCell-stickyHeader': {
            backgroundColor: '#0B1120',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.03) !important',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#141821',
          border: '1px solid rgba(255,255,255,0.08)',
          '@media (max-width:600px)': {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: '#9CA3AF',
          '&.Mui-selected': {
            color: '#00FF94',
          },
          '@media (max-width:600px)': {
            minWidth: 'auto',
            padding: '6px 12px',
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#00FF94',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            height: 24,
            fontSize: '0.7rem',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(255,255,255,0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255,255,255,0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00FF94',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.1)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.2)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00FF94',
          },
        },
      },
    },
  },
});

// Helper function for role-based routing
const RoleBasedRedirect = () => {
  // In a real app, this would check auth context
  // For demo, we're setting admin as the role
  const userRole = "admin"; // or 'user'

  // Redirect to strategy builder for quick access to strategy management
  if (userRole === "admin") {
    return <Navigate to="/user/strategy-builder" replace />;
  } else {
    return <Navigate to="/user/strategy-builder" replace />;
  }
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public Routes - No layout */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Auth Routes - Backward compatibility */}
          <Route
            path="/auth/login"
            element={<Navigate to="/login" replace />}
          />
          <Route
            path="/auth/register"
            element={<Navigate to="/register" replace />}
          />

          {/* Root redirect - sends to appropriate dashboard based on role */}
          <Route path="/" element={<RoleBasedRedirect />} />

          {/* User Area - completely separate section */}
          <Route path="/user" element={<Layout />}>
            <Route path="dashboard" element={<UserDashboard />} />
            <Route path="plans" element={<UserPlans />} />
            <Route path="tickets" element={<UserTickets />} />
            <Route path="settings" element={<UserSettings />} />
            <Route path="api-connections" element={<ApiConnections />} />

            {/* Trading tools accessible from user area */}
            <Route path="technical" element={<TechnicalAnalysis />} />
            <Route path="fundamental" element={<FundamentalAnalysis />} />
            <Route path="risk" element={<RiskManagement />} />
            <Route path="history" element={<TradeHistory />} />
            <Route path="execution" element={<Execution />} />
            <Route path="strategy-builder" element={<StrategyBuilder />} />
            <Route
              path="tradingview-webhook"
              element={<TradingViewWebhook />}
            />
            <Route path="documentation" element={<Documentation />} />
            <Route path="analytics" element={<TradingDashboard />} />
            <Route path="strategy-dashboard" element={<StrategyDashboard />} />
            {process.env.REACT_APP_ENABLE_MONITOR_PAGE !== 'false' && (
              <Route path="monitor" element={<MonitorErrorBoundary><Monitor /></MonitorErrorBoundary>} />
            )}
          </Route>

          {/* Admin Area - completely separate section */}
          <Route path="/admin" element={<Layout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="marketing" element={<AdminMarketing />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="analytics" element={<TradingDashboard />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
